import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Clipboard} from '@angular/cdk/clipboard';
import {FileSystemFileEntry, NgxFileDropEntry} from 'ngx-file-drop';
import {DataService} from 'src/app/services/data.service';
import {FileService} from 'src/app/services/file.service';
import {DialogService} from 'src/app/services/dialog.service';
import {TrustiiService} from 'src/app/services/trustii.service';
import {catchError, map, tap, filter, distinctUntilChanged} from 'rxjs/operators';
import {Observable, throwError} from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-prediction-upload',
  templateUrl: './predictions-upload.component.html',
  styleUrls: ['./predictions-upload.component.scss']
})
export class PredictionsUploadComponent implements OnInit {
  filename = null;
  files = null;
  fileToBeUploaded = null;
  fileSubmitted = false;
  notebookFilename = null;
  notebookFiles = null;
  notebookFileToBeUploaded = null;
  notebookFileSubmitted = false;
  termsAccepted = false;
  uploading = false;
  uploadedPercentage = null;
  ready = false;
  dataset: any;
  datasetId: number;
  predictionId: number;
  remainingSubmissions = 10;
  remainingTime = 0;

  constructor(
    private fileService: FileService, private dataservice: DataService, private clipboard: Clipboard, private snackBar: MatSnackBar,
    private dialogService: DialogService, private router: Router, private trustiiService: TrustiiService
  ) {
  }

  ngOnInit() {
    this.datasetId = this.dataservice.data.vendorDownloadedDatasetId;
    this._loadDataset$().subscribe();
    this.remainingTime = 24 - (new Date()).getUTCHours();
  }

  /** _loadDataset$ method
   *
   * Load dataset if there is id dataset on params.
   */
  private _loadDataset$(): Observable<any> {
    return this.trustiiService.getDataset(this.datasetId)
      .pipe(
        map((dataset: any) => this.dataset = dataset.data),
        tap(() => this.remainingSubmissions = (this.dataset.dsNbSubmissions) ?
          this.remainingSubmissions - this.dataset.dsNbSubmissions : 10),
        tap(() => this.remainingSubmissions = (this.remainingSubmissions > 0) ? this.remainingSubmissions : 0),
        tap(() => this.ready = true),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  submitPrediction() {
    if (this.fileToBeUploaded === null) {
      this.fileSubmitted = false;
      this.fileToBeUploaded = null;
      this.dialogService.openAlertDialog(
        '350px',
        'Please browse or drop a file in the drop box.',
        'ERROR',
        'Ok',
        'modal-warning'
      );
      return;
    }

    this.fileService.file = this.fileToBeUploaded;
    this.sendPrediction();
  }

  public dropped(files: NgxFileDropEntry[]) {
    this.files = files;
    const numberOfFile = files.length;
    if (numberOfFile > 1) {
      this.dialogService.openAlertDialog(
        '350px',
        'Please submit only one model at a time.',
        'ERROR',
        'Ok',
        'modal-warning'
      );
      return;
    }
    this.files.forEach((droppedItem, index) => {
      if (!droppedItem.fileEntry.isFile) {
        this.dialogService.openAlertDialog(
          '350px',
          '"' + droppedItem.relativePath + '" is not a file.',
          'ERROR',
          'Ok',
          'modal-warning'
        );
        return;
      }
      const fileEntry = droppedItem.fileEntry as FileSystemFileEntry;
      fileEntry.file((file: File) => {
        this.filename = file.name;
        if (file.name.split('.').pop().toLowerCase() !== 'csv') {
          this.dialogService.openAlertDialog(
            '350px',
            '"' + droppedItem.relativePath + '" is not supported.<br /> We support only csv submission as of now.',
            'ERROR',
            'Ok',
            'modal-warning'
          );
          return;
        }
        if (file.size > (this.fileService.getFleSizeLimit() * 2000)) {
          this.dialogService.openAlertDialog(
            '350px',
            '"' + droppedItem.relativePath + '" exceeds the supported size limit. we do not support file size more than ' +
            '2GB as of today. Please try again with file size within limit.',
            'ERROR',
            'Ok',
            'modal-warning'
          );
          return;
        }
        this.fileToBeUploaded = file;
        this.fileSubmitted = true;
      });
    });
  }

  public notebookDropped(files: NgxFileDropEntry[]) {
    this.notebookFiles = files;
    const numberOfFile = files.length;
    if (numberOfFile > 1) {
      this.dialogService.openAlertDialog(
        '350px',
        'Please submit only one notebook at a time.',
        'ERROR',
        'Ok',
        'modal-warning'
      );
      return;
    }
    this.notebookFiles.forEach((droppedItem, index) => {
      if (!droppedItem.fileEntry.isFile) {
        this.dialogService.openAlertDialog(
          '350px',
          '"' + droppedItem.relativePath + '" is not a file.',
          'ERROR',
          'Ok',
          'modal-warning'
        );
        return;
      }
      const fileEntry = droppedItem.fileEntry as FileSystemFileEntry;
      fileEntry.file((file: File) => {
        this.notebookFilename = file.name;
        const extension = file.name.split('.').pop().toLowerCase();
        if (extension !== 'ipynb' && extension !== 'zip') {
          this.dialogService.openAlertDialog(
            '350px',
            '"' + droppedItem.relativePath + '" is not supported.<br />We support only ipynb or zip submission as of now.',
            'ERROR',
            'Ok',
            'modal-warning'
          );
          return;
        }
        if (file.size > (this.fileService.getFleSizeLimit() * 2000)) {
          this.dialogService.openAlertDialog(
            '350px',
            '"' + droppedItem.relativePath + '" exceeds the supported size limit. we do not support file size more than ' +
            '2GB as of today. Please try again with file size within limit.',
            'ERROR',
            'Ok',
            'modal-warning'
          );
          return;
        }
        this.notebookFileToBeUploaded = file;
        this.notebookFileSubmitted = true;
      });
    });
  }

  public fileOver(event) {
  }

  public fileLeave(event) {
  }

  sendPrediction() {
    this.uploading = true;
    const formData = new FormData();
    formData.append('predictionFile', this.fileService.file, this.fileService.file.name);
    if (this.notebookFileToBeUploaded) {
      formData.append('notebookFile', this.notebookFileToBeUploaded, this.notebookFileToBeUploaded.name);
    }
    /*if (this.dataset.withNotebook === 'yes' && !this.notebookFileToBeUploaded) {
      this.dialogService.openAlertDialog(
        '350px',
        'Notebook is mandatory.',
        'ERROR',
        'Ok',
        'modal-warning'
      );
    }*/
    this.trustiiService.createPrediction(formData, this.datasetId).subscribe(
      (res) => {
        if (res.status === 'progress') {
          this.uploadedPercentage = res.message;
        } else if (res.status === 'error') {
          this.retry();
        } else if (res.data) {
          this.uploading = false;
          if ('error' === res.data.status) {
            this.fileSubmitted = false;
            this.fileToBeUploaded = null;
            this.notebookFileToBeUploaded = null;
            this.notebookFileSubmitted = false;
            this.dialogService.openAlertDialog(
              '350px',
              res.data.error
                ? res.data.error
                : 'An error occured during predictions analysis.',
              'ERROR',
              'Ok',
              'modal-warning'
            );
          } else {
            this.dialogService.openAlertDialog(
              '350px',
              `Your submission have been successfully uploaded, you will be able to check your score after the submission analysis.`,
              'SUCCESS',
              'Ok',
              'modal-success'
            );
            this.router.navigateByUrl(`vendor/datasets/${this.datasetId}/predictions`);
          }
        }
      },
      (err) => {
        this.retry();
      }
    );
  }

  retry(): void {
    this.uploading = false;
    this.dialogService.openConfirmDialog('Failed to upload Prediction. Retry?').pipe(
      distinctUntilChanged(),
      tap((result) => {
        if (result && result.data === 'confirmation') {
          this.sendPrediction();
        } else {
          this.uploading = false;
          this.fileSubmitted = false;
          this.fileToBeUploaded = null;
          this.notebookFileToBeUploaded = null;
          this.notebookFileSubmitted = false;
        }
      })
    ).subscribe();
  }

  copyDatasetId() {
    this.clipboard.copy(this.datasetId.toString());
    this.snackBar.open('Dataset ID properly copied!', '', {
      duration: 2000,
    });
  }

  copyToken() {
    const token = localStorage.getItem('token');
    this.clipboard.copy(token);
    this.snackBar.open('Token properly copied!', '', {
      duration: 2000,
    });
  }
}
